import { useState, useEffect } from 'react';
import { useMergeContacts } from '@/hooks/useMergeContacts';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { Loader2, AlertTriangle, CheckCircle2, Database, RefreshCw, Zap } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { AutoMergeDuplicates } from '@/components/contacts/AutoMergeDuplicates';

type MergeStrategy = 'keep_oldest' | 'keep_newest' | 'keep_most_complete';

export default function MergeContacts() {
  const {
    loading, 
    duplicates, 
    findDuplicates, 
    mergeAllDuplicates,
    createBackup,
  } = useMergeContacts();

  const [strategy, setStrategy] = useState<MergeStrategy>('keep_most_complete');
  const [batchSize, setBatchSize] = useState(50);
  const [progress, setProgress] = useState(0);
  const [isMerging, setIsMerging] = useState(false);
  const [backupCreated, setBackupCreated] = useState(false);
  const [mergeResults, setMergeResults] = useState<{
    successful: number;
    failed: number;
    total: number;
  } | null>(null);

  useEffect(() => {
    findDuplicates();
  }, []);

  const handleCreateBackup = async () => {
    try {
      await createBackup();
      setBackupCreated(true);
    } catch (err) {
      console.error('Backup failed:', err);
    }
  };

  const handleMergeLucas = async () => {
    if (!confirm('Confirma o merge do Lucas Costa? Manterá o contato com 66 calls/messages.')) {
      return;
    }

    setIsMerging(true);
    try {
      const { data, error } = await supabase.functions.invoke('merge-duplicate-contacts', {
        body: { action: 'merge_lucas' }
      });

      if (error) throw error;

      toast.success('Lucas Costa merged com sucesso!');
      await findDuplicates(); // Recarregar lista
    } catch (err) {
      console.error('Error merging Lucas:', err);
      toast.error('Erro ao fazer merge do Lucas Costa');
    } finally {
      setIsMerging(false);
    }
  };

  const handleMergeAllServer = async () => {
    const totalDuplicates = duplicates.reduce((sum, group) => sum + group.contacts.length - 1, 0);
    
    if (!confirm(
      `⚠️ MERGE AUTOMÁTICO DE TODOS OS GRUPOS\n\n` +
      `• ${duplicates.length} grupos de duplicatas\n` +
      `• ${totalDuplicates} contatos serão mesclados\n` +
      `• Estratégia: Manter contato mais completo (keep_most_complete)\n` +
      `• Preserva: deals, messages, calls, emails, phones\n\n` +
      `Esta ação NÃO pode ser desfeita!\n\n` +
      `Tem certeza que deseja continuar?`
    )) {
      return;
    }

    setIsMerging(true);
    setProgress(0);
    
    try {
      toast.info('Iniciando merge automático... Isso pode levar alguns minutos.');
      
      const { data, error } = await supabase.functions.invoke('merge-duplicate-contacts', {
        body: { action: 'merge_all' }
      });

      if (error) throw error;

      setMergeResults({
        successful: data.processed,
        failed: data.errors,
        total: data.total
      });
      
      toast.success(`✅ Merge concluído! ${data.processed} grupos processados, ${data.errors} erros`);
      await findDuplicates(); // Recarregar lista
    } catch (err) {
      console.error('Error in bulk merge:', err);
      toast.error('❌ Erro ao executar merge em massa');
    } finally {
      setIsMerging(false);
      setProgress(100);
    }
  };

  const handleMergeAll = async () => {
    if (!backupCreated) {
      alert('Por favor, crie um backup antes de fazer o merge!');
      return;
    }

    if (!confirm(`Tem certeza que deseja mesclar ${duplicates.length} grupos de duplicatas?`)) {
      return;
    }

    setIsMerging(true);
    setProgress(0);
    
    try {
      const results = await mergeAllDuplicates(
        strategy,
        duplicates.slice(0, batchSize),
        (current, total) => {
          setProgress((current / total) * 100);
        }
      );
      
      setMergeResults(results);
    } catch (err) {
      console.error('Merge failed:', err);
    } finally {
      setIsMerging(false);
    }
  };

  const getStrategyDescription = (strat: MergeStrategy) => {
    switch (strat) {
      case 'keep_oldest':
        return 'Mantém o contato mais antigo (primeira criação)';
      case 'keep_newest':
        return 'Mantém o contato mais recente (última criação)';
      case 'keep_most_complete':
        return 'Mantém o contato com mais informações preenchidas e relacionamentos';
      default:
        return '';
    }
  };

  if (loading && duplicates.length === 0) {
    return (
      <div className="flex items-center justify-center flex-1">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Merge de Contatos Duplicados</h1>
          <p className="text-muted-foreground">
            Gerencie e mescle contatos duplicados em massa
          </p>
        </div>
        <Button
          variant="outline"
          onClick={() => findDuplicates()}
          disabled={loading}
        >
          <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Atualizar
        </Button>
      </div>

      {/* Stats */}
      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Grupos de Duplicatas</CardTitle>
            <AlertTriangle className="h-4 w-4 text-warning" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{duplicates.length}</div>
            <p className="text-xs text-muted-foreground">
              Grupos com 2+ contatos duplicados
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total de Duplicatas</CardTitle>
            <Database className="h-4 w-4 text-primary" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {duplicates.reduce((acc, group) => acc + group.contacts.length, 0)}
            </div>
            <p className="text-xs text-muted-foreground">
              Contatos duplicados no total
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Status do Backup</CardTitle>
            <CheckCircle2 className={`h-4 w-4 ${backupCreated ? 'text-success' : 'text-muted-foreground'}`} />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {backupCreated ? 'Criado' : 'Não criado'}
            </div>
            <p className="text-xs text-muted-foreground">
              Backup antes do merge
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Merge Automático - Componente Otimizado */}
      <AutoMergeDuplicates onMergeComplete={findDuplicates} />

      {/* Quick Actions Secundárias */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Zap className="h-5 w-5" />
            Ações Específicas
          </CardTitle>
          <CardDescription>
            Execute merges específicos individuais
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Button
            onClick={handleMergeLucas}
            disabled={isMerging || loading}
            variant="outline"
            className="w-full"
          >
            {isMerging ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Zap className="h-4 w-4 mr-2" />
            )}
            Merge Lucas Costa (específico)
          </Button>
          
          <Alert className="mt-4">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>
              <strong>Estratégia automática:</strong> Os merges automáticos usam "keep_most_complete"
              e NÃO PODEM SER DESFEITOS. Tenha certeza antes de executar!
            </AlertDescription>
          </Alert>
        </CardContent>
      </Card>

      {/* Backup Alert */}
      {!backupCreated && (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>Backup Necessário</AlertTitle>
          <AlertDescription className="flex items-center justify-between">
            <span>Crie um backup antes de fazer o merge em massa</span>
            <Button onClick={handleCreateBackup} size="sm" variant="outline">
              Criar Backup
            </Button>
          </AlertDescription>
        </Alert>
      )}

      {/* Merge Results */}
      {mergeResults && (
        <Alert>
          <CheckCircle2 className="h-4 w-4" />
          <AlertTitle>Merge Concluído</AlertTitle>
          <AlertDescription>
            <div className="mt-2 space-y-1">
              <p>✅ Sucesso: {mergeResults.successful} grupos</p>
              <p>❌ Falhas: {mergeResults.failed} grupos</p>
              <p>📊 Total: {mergeResults.total} grupos processados</p>
            </div>
          </AlertDescription>
        </Alert>
      )}

      {/* Merge Configuration */}
      <Card>
        <CardHeader>
          <CardTitle>Configuração do Merge</CardTitle>
          <CardDescription>
            Escolha a estratégia e o tamanho do lote para processar
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <label className="text-sm font-medium">Estratégia de Merge</label>
              <Select
                value={strategy}
                onValueChange={(value) => setStrategy(value as MergeStrategy)}
                disabled={isMerging}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="keep_most_complete">
                    Manter Mais Completo
                  </SelectItem>
                  <SelectItem value="keep_oldest">
                    Manter Mais Antigo
                  </SelectItem>
                  <SelectItem value="keep_newest">
                    Manter Mais Recente
                  </SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                {getStrategyDescription(strategy)}
              </p>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">Tamanho do Lote</label>
              <Select
                value={batchSize.toString()}
                onValueChange={(value) => setBatchSize(parseInt(value))}
                disabled={isMerging}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="10">10 grupos (teste)</SelectItem>
                  <SelectItem value="50">50 grupos</SelectItem>
                  <SelectItem value="100">100 grupos</SelectItem>
                  <SelectItem value={duplicates.length.toString()}>
                    Todos ({duplicates.length} grupos)
                  </SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                Processar em lotes para melhor controle
              </p>
            </div>
          </div>

          {isMerging && (
            <div className="space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span>Processando...</span>
                <span>{Math.round(progress)}%</span>
              </div>
              <Progress value={progress} />
            </div>
          )}

          <Button
            onClick={handleMergeAll}
            disabled={!backupCreated || isMerging || duplicates.length === 0}
            className="w-full"
            size="lg"
          >
            {isMerging ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Mesclando...
              </>
            ) : (
              `Mesclar ${Math.min(batchSize, duplicates.length)} Grupos`
            )}
          </Button>
        </CardContent>
      </Card>

      {/* Duplicates List */}
      <Card>
        <CardHeader>
          <CardTitle>Grupos de Duplicatas ({duplicates.length})</CardTitle>
          <CardDescription>
            Preview dos primeiros 20 grupos
          </CardDescription>
        </CardHeader>
        <CardContent>
          <ScrollArea className="h-[400px]">
            <div className="space-y-4">
              {duplicates.slice(0, 20).map((group, idx) => (
                <div key={idx} className="border rounded-lg p-4 space-y-2">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Badge variant="outline">
                        {group.phone}
                      </Badge>
                      <Badge>
                        {group.contacts.length} contatos
                      </Badge>
                    </div>
                  </div>
                  
                  <Separator />
                  
                  <div className="space-y-2">
                    {group.contacts.map((contact) => (
                      <div 
                        key={contact.id} 
                        className="flex items-center justify-between text-sm bg-muted/50 rounded p-2"
                      >
                        <div>
                          <span className="font-medium">
                            {contact.first_name} {contact.last_name}
                          </span>
                          {contact.primary_email && (
                            <span className="text-muted-foreground ml-2">
                              • {contact.primary_email}
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-2 text-xs text-muted-foreground">
                          <span>
                            {contact.created_at && formatDistanceToNow(new Date(contact.created_at), {
                              addSuffix: true,
                              locale: ptBR
                            })}
                          </span>
                          {(contact.deals_count || 0) > 0 && (
                            <Badge variant="secondary" className="text-xs">
                              {contact.deals_count} deals
                            </Badge>
                          )}
                          {(contact.messages_count || 0) > 0 && (
                            <Badge variant="secondary" className="text-xs">
                              {contact.messages_count} msgs
                            </Badge>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </ScrollArea>
        </CardContent>
      </Card>
    </div>
  );
}
